import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import Badge from 'components/Badge';
import Card from 'components/Card';
import DetailSection from 'components/DetailSection';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { Input } from 'components/FormFields';
import Icon from 'components/Icon';
import InfoCard from 'components/InfoCard';
import PageHeaderIcon from 'components/PageHeaderIcon';
import QuickActions from 'components/QuickActions';
import { WaiverNotSigned } from 'components/SVG/WaiverNotSigned';
import TableSkeleton from 'components/TableSkeleton';
import { DownloadMemberWaiverButton } from 'pages/app/AnnualTickets/Members/MemberDetail/Components/DownloadWaiver';
import { useGetMember } from 'pages/app/AnnualTickets/Members/MemberDetail/gql/useGetMember';
import PermissionModal from 'pages/track/PermissionModal/PermissionModal';
import { AppContext } from 'shared/AppContext';
import { formatPhoneNumber } from 'shared/formatters';
import { useMembership } from 'shared/membershipContext';
import { useUpdateRegistration } from './gql';

// Page Container
const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background || '#0F172A'
      : '#FFFFFF'};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Header components
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
`;

const HeaderSubtitle = styled.p`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 4px 0 0 0;
`;

// Content wrapper
const ContentWrapper = styled.div`
  padding: 32px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const DashboardLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 30px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const WaiversGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const WaiverItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 8px;
`;

const WaiverName = styled.span`
  font-size: 12px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  text-align: center;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const FormTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  margin: 0 0 16px 0;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const UpdateButton = styled.button`
  padding: 10px 24px;
  background: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Helper to convert snake_case to Title Case
const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Forms Repeater Component
const FormsRepeaterSection = ({ formData }) => {
  const updateRegistration = useUpdateRegistration();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({});
  const history = useHistory();
  const { pathname } = useLocation();

  const {
    state: { capabilities },
  } = useContext(AppContext);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  const userType = pathname.includes('/admin/')
    ? 'admin'
    : pathname.split('/').includes('admin-employee')
    ? 'employee'
    : 'track';

  useEffect(() => {
    if (formData?.data) {
      const values = formData.data.reduce(
        (acc, item) => ({
          ...acc,
          [item.name]: item.value || '',
        }),
        {}
      );
      setFormValues(values);
    }
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (capabilities && !capabilities.edit_memberships) {
      setShowPermissionModal(true);
      return;
    }

    setIsSubmitting(true);

    const input = {
      id: formData.id,
      fields: Object.entries(formValues).map(([name, value]) => ({
        name,
        value,
      })),
    };

    try {
      const response = await updateRegistration(input);
      if (response && !response.errors) {
        toast.success('Form updated successfully');
      }
    } catch (error) {
      toast.error('Failed to update form');
    }

    setIsSubmitting(false);
  };

  if (!formData?.data?.length) return null;

  return (
    <FormSection>
      <FormTitle>{formData.name}</FormTitle>
      <form onSubmit={handleSubmit}>
        <FormGrid>
          {formData.data.map((field, index) => (
            <Input
              key={index}
              id={field.name}
              name={field.name}
              label={toTitleCase(field.name)}
              value={formValues[field.name] || ''}
              onChange={handleChange}
            />
          ))}
        </FormGrid>
        <ButtonWrapper>
          <UpdateButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Updating...' : 'Update Form'}
          </UpdateButton>
        </ButtonWrapper>
      </form>

      <PermissionModal
        isVisible={showPermissionModal}
        hideModal={() => setShowPermissionModal(false)}
        link={`/admin-${userType}/settings/1`}
        history={history}
      />
    </FormSection>
  );
};

export const TrackRegistrationDetail = () => {
  const history = useHistory();
  const { pathname } = useLocation();

  const isTrackAdmin = pathname.includes('/admin-track');
  const isEmployeeAdmin = pathname.includes('/admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const { data, loading, refetch } = useGetMember();
  const { fetchMember, setFetchMember } = useMembership();

  useEffect(() => {
    if (fetchMember) {
      refetch();
      setFetchMember(false);
    }
  }, [fetchMember, refetch, setFetchMember]);

  const member = data?.getMember;
  const user = member?.user;

  // Format purchase date
  const formatPurchaseDate = (purchaseDate) => {
    if (!purchaseDate) return '-';
    const now = moment(purchaseDate, 'MM-DD-YYYY HH:mm:ss.SSSZ');
    const momentInSpecificTimezone = now.tz('America/New_York');
    return momentInSpecificTimezone.format('MMM DD, YYYY h:mm A') + ' EST';
  };

  if (loading) {
    return (
      <EnhancedThemeProvider>
        <PageContainer>
          <Card style={{ marginBottom: '30px' }}>
            <HeaderContainer>
              <PageHeaderIcon icon="events" />
              <HeaderTitle>Registration Detail</HeaderTitle>
            </HeaderContainer>
          </Card>
          <Card>
            <ContentWrapper>
              <TableSkeleton rows={8} columns={2} />
            </ContentWrapper>
          </Card>
        </PageContainer>
      </EnhancedThemeProvider>
    );
  }

  if (!member) {
    return (
      <EnhancedThemeProvider>
        <PageContainer>
          <Card>
            <ContentWrapper>
              <p>Registration not found</p>
            </ContentWrapper>
          </Card>
        </PageContainer>
      </EnhancedThemeProvider>
    );
  }

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="events" />
            <div>
              <HeaderTitle>Registration Detail</HeaderTitle>
              <HeaderSubtitle>
                {user?.formatted_name || user?.name || 'Registration'} -{' '}
                {member.membership?.name}
              </HeaderSubtitle>
            </div>
          </HeaderContainer>
        </Card>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Registration Info Card */}
            <Card>
              <ContentWrapper>
                <DetailSection title="Registration Information">
                  <DetailSection.Grid>
                    <DetailSection.Item
                      label="Full Name"
                      value={user?.formatted_name || user?.name || '-'}
                    />
                    <DetailSection.Item
                      label="Registration Type"
                      value={member.membership?.name || '-'}
                    />
                    <DetailSection.Item
                      label="Division/Class"
                      value={member.divison || '-'}
                    />
                    <DetailSection.Item
                      label="Purchase Date"
                      value={formatPurchaseDate(member.purchase?.purchase_date)}
                    />
                    {member.serial_number && (
                      <DetailSection.Item
                        label="Serial Number"
                        value={member.serial_number}
                      />
                    )}
                    {(member.section || member.row || member.seat) && (
                      <DetailSection.Item
                        label="Reserved"
                        value={[member.section, member.row, member.seat]
                          .filter(Boolean)
                          .join(', ')}
                      />
                    )}
                  </DetailSection.Grid>
                </DetailSection>
              </ContentWrapper>
            </Card>

            {/* User Info Card */}
            <Card>
              <ContentWrapper>
                <DetailSection title="Pit Pay User Info">
                  <DetailSection.Grid>
                    <DetailSection.Item
                      label="Last Name"
                      value={user?.last_name}
                    />
                    <DetailSection.Item
                      label="First Name"
                      value={user?.first_name}
                    />
                    <DetailSection.Item
                      label="Middle Name"
                      value={user?.middle_name}
                    />
                    <DetailSection.Item label="Suffix" value={user?.suffix} />
                    <DetailSection.Item
                      label="Mobile Number"
                      value={
                        user?.cellphone
                          ? formatPhoneNumber(user.cellphone)
                          : '-'
                      }
                    />
                    <DetailSection.Item label="Email" value={user?.email} />
                    <DetailSection.Item
                      label="Date of Birth"
                      value={user?.birthday}
                    />
                    <DetailSection.Item
                      label="Address Line 1"
                      value={user?.address_1}
                    />
                    <DetailSection.Item
                      label="Address Line 2"
                      value={user?.address_2}
                    />
                    <DetailSection.Item label="City" value={user?.city} />
                    <DetailSection.Item
                      label="State / Province"
                      value={user?.state}
                    />
                    <DetailSection.Item
                      label="Postal Code"
                      value={user?.zipcode}
                    />
                  </DetailSection.Grid>
                </DetailSection>
              </ContentWrapper>
            </Card>

            {/* Forms Card */}
            {member.forms && member.forms.length > 0 && (
              <Card>
                <ContentWrapper>
                  <DetailSection title="Forms" noMargin>
                    {member.forms.map((form) => (
                      <FormsRepeaterSection
                        key={form.id}
                        formData={form}
                        routePrefix={routePrefix}
                      />
                    ))}
                  </DetailSection>
                </ContentWrapper>
              </Card>
            )}

            {/* Agreements Card */}
            {member.waivers && member.waivers.length > 0 && (
              <Card>
                <ContentWrapper>
                  <DetailSection title="Agreements" noMargin>
                    <WaiversGrid>
                      {member.waivers.map((waiver) => (
                        <WaiverItem key={waiver.id}>
                          {!waiver.is_signed ? (
                            <WaiverNotSigned
                              width={40}
                              style={{ padding: 5 }}
                            />
                          ) : (
                            <DownloadMemberWaiverButton
                              waiver_id={waiver.id}
                              icon={
                                <Icon
                                  icon="Waiver-Icon"
                                  color="#FA4616"
                                  size={40}
                                />
                              }
                              transparent={true}
                            />
                          )}
                          <WaiverName>{waiver.name}</WaiverName>
                          <Badge
                            variant={waiver.is_signed ? 'success' : 'error'}
                            size="small"
                          >
                            {waiver.is_signed ? 'Signed' : 'Not Signed'}
                          </Badge>
                        </WaiverItem>
                      ))}
                    </WaiversGrid>
                  </DetailSection>
                </ContentWrapper>
              </Card>
            )}
          </MainContent>

          <Sidebar>
            {/* Summary Card */}
            <Card>
              <InfoCard>
                <InfoCard.Row
                  label="Registration"
                  value={member.membership?.name || '-'}
                />
                <InfoCard.Row label="Division" value={member.divison || '-'} />
                <InfoCard.Row label="Forms" value={member.forms?.length || 0} />
                <InfoCard.Row
                  label="Agreements"
                  value={member.waivers?.length || 0}
                />
              </InfoCard>
            </Card>

            {/* Quick Actions */}
            <QuickActions
              title="Quick Actions"
              actions={[
                {
                  label: 'Back to Event',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fillRule="evenodd"
                        d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"
                      />
                    </svg>
                  ),
                  onClick: () => history.goBack(),
                },
              ]}
            />
          </Sidebar>
        </DashboardLayout>
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackRegistrationDetail;
